import { useSnackbar } from "notistack"
import { useState } from "react"

// @mui
import { Box, Divider, MenuItem, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
// routes
import { IconButtonAnimate } from "minimals-template/components/animate"
import MenuPopover from "minimals-template/components/MenuPopover"
import MyAvatar from "minimals-template/components/MyAvatar"
import { PATH_AUTH, PATH_DASHBOARD } from "routes/paths"
// hooks
import { RouterLink } from "lib/routes/router-link"
import useAuth from "minimals-template/components/@hooks/useAuth"
import useIsMountedRef from "minimals-template/components/@hooks/useIsMountedRef"
import { useCurrentUser } from "lib/store/use-current-user"
import { NotInSharing } from "lib/@components/sharing/not-in-sharing"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { navigate } from "lib/routes/navigate"
import { toDataCy } from "lib/to-data-cy"
import { getTopLevelPath } from "lib/getTopLevelPath"
import { guardLogout } from "lib/@offline/guard-logout"
// components

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
    {
        label: "Home",
        linkTo: PATH_DASHBOARD.root ?? "/app",
    },
]

// ----------------------------------------------------------------------

export default function AccountPopover({ menuOptions = [] }) {
    const options = mergeOptions(menuOptions)

    const { logout } = useAuth()
    const user = useCurrentUser()
    const [shareLinkId] = useSearchParam("share", null)

    const isMountedRef = useIsMountedRef()

    const { enqueueSnackbar } = useSnackbar()

    const [open, setOpen] = useState(null)

    const handleOpen = (event) => {
        setOpen(event.currentTarget)
    }

    const handleClose = () => {
        setOpen(null)
    }

    const handleLogout = async () => {
        try {
            if (!(await guardLogout())) return
            await logout()
            navigate(PATH_AUTH.login, { replace: true })

            if (isMountedRef.current) {
                handleClose()
            }
        } catch (error) {
            console.error(error)
            enqueueSnackbar("Unable to logout!", { variant: "error" })
        }
    }

    return (
        <>
            <IconButtonAnimate
                aria-label="Open account menu"
                onClick={handleOpen}
                id="account-menu"
                data-cy="account-menu"
                sx={{
                    p: 0,
                    ...(open && {
                        "&:before": {
                            zIndex: 1,
                            content: "''",
                            width: "100%",
                            height: "100%",
                            borderRadius: "50%",
                            position: "absolute",
                            bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
                        },
                    }),
                }}
            >
                <MyAvatar data-cy="account-popover-avatar" />
            </IconButtonAnimate>

            <MenuPopover
                open={Boolean(open)}
                anchorEl={open}
                onClose={handleClose}
                sx={{
                    p: 0,
                    mt: 1.5,
                    ml: 0.75,
                    "& .MuiMenuItem-root": {
                        typography: "body2",
                        borderRadius: 0.75,
                    },
                }}
            >
                <Box sx={{ my: 1.5, px: 2.5 }} data-cy="account-popover-user-info">
                    <Typography variant="subtitle2" noWrap>
                        {user?.metadata?.displayName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                        {user?.email}
                    </Typography>
                </Box>

                <Divider sx={{ borderStyle: "dashed" }} />

                <Stack sx={{ p: 1 }}>
                    <NotInSharing>
                        {options.map((option) => (
                            <MenuItem
                                data-cy={`${toDataCy("account-popover-menu-item", option.label)}`}
                                key={option.label}
                                to={option.linkTo}
                                component={RouterLink}
                                onClick={handleClose}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </NotInSharing>
                    <MenuItem onClick={handleAccount} data-cy="account-popover-account-menu-item">
                        Profile
                    </MenuItem>
                </Stack>

                <Divider sx={{ borderStyle: "dashed" }} />

                <MenuItem onClick={handleLogout} sx={{ m: 1 }} data-cy="account-popover-menu-item-logout">
                    Logout
                </MenuItem>
            </MenuPopover>
        </>
    )

    function handleAccount() {
        handleClose()
        const shareQuery = shareLinkId ? `?share=${encodeURIComponent(shareLinkId)}` : ""
        navigate(`/${getTopLevelPath()}/account${shareQuery}`)
    }

    function mergeOptions(overrides) {
        return MENU_OPTIONS.map((option) => {
            const override = overrides.find((o) => o.label === option.label)
            return override ? { ...option, ...override } : option
        })
    }
}
